import React, { useState } from "react";
import styled from "styled-components";
import { State, GuideLayer } from "../types";
import { TemplateFile } from "../types/templates";

interface TemplateExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  states: State[];
  guideLayers?: GuideLayer[];
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 44px;
  align-items: center;

  &:focus-within {
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const Tag = styled.span`
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RemoveTagButton = styled.button`
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-left: 4px;

  &:hover {
    color: #d32f2f;
  }
`;

const TagInputField = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 100px;
  font-size: 14px;
`;

const StatesPreview = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
`;

const StateItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const StateColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.color};
  margin-right: 12px;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const StateInfo = styled.div`
  flex: 1;
`;

const StateName = styled.div`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const StateDescription = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  background: ${(props) => {
    if (props.variant === "primary") return "#2196f3";
    return "#f5f5f5";
  }};

  color: ${(props) => {
    if (props.variant === "primary") return "white";
    return "#333";
  }};

  &:hover {
    background: ${(props) => {
      if (props.variant === "primary") return "#1976d2";
      return "#e0e0e0";
    }};
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

const TemplateExportModal: React.FC<TemplateExportModalProps> = ({
  isOpen,
  onClose,
  states,
  guideLayers = [],
}) => {
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");

  // Initialize form with default values when states change
  React.useEffect(() => {
    if (states.length > 0 || guideLayers.length > 0) {
      setTemplateName(`Custom Map - ${new Date().toLocaleDateString()}`);
      setTemplateDescription(
        `Custom map with ${states.length} states and ${guideLayers.length} guide layers`
      );
      setTemplateTags(["custom", "user-created"]);
    }
  }, [states, guideLayers]);

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && newTag.trim()) {
      e.preventDefault();
      if (!templateTags.includes(newTag.trim())) {
        setTemplateTags([...templateTags, newTag.trim()]);
      }
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTemplateTags(templateTags.filter((tag) => tag !== tagToRemove));
  };

  const handleExport = () => {
    if (!templateName.trim()) {
      alert("Please enter a template name");
      return;
    }

    const template: TemplateFile = {
      apiVersion: "v1",
      kind: "MapTemplate",
      id: `custom-${Date.now()}`,
      name: templateName.trim(),
      description: templateDescription.trim(),
      tags: templateTags,
      states: states.map((state) => ({
        id: state.id,
        name: state.name,
        description: state.description || "",
        population: state.population || 0,
        capital: state.capital || "",
        color: state.color,
        createdAt: state.createdAt,
        coordinates: state.coordinates,
      })),
      guideLayers: guideLayers.length > 0 ? guideLayers : undefined,
      createdAt: new Date().toISOString(),
    };

    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${templateName
      .replace(/[^a-z0-9]/gi, "-")
      .toLowerCase()}-template.json`;
    link.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Export Template</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <FormGroup>
          <Label>Template Name *</Label>
          <Input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name"
          />
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Describe your template"
          />
        </FormGroup>

        <FormGroup>
          <Label>Tags</Label>
          <TagInput>
            {templateTags.map((tag) => (
              <Tag key={tag}>
                {tag}
                <RemoveTagButton onClick={() => handleRemoveTag(tag)}>
                  ×
                </RemoveTagButton>
              </Tag>
            ))}
            <TagInputField
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={handleAddTag}
              placeholder="Add tag and press Enter"
            />
          </TagInput>
        </FormGroup>

        <StatesPreview>
          <Label>States to Export ({states.length})</Label>
          {states.map((state) => (
            <StateItem key={state.id}>
              <StateColor color={state.color} />
              <StateInfo>
                <StateName>{state.name}</StateName>
                <StateDescription>
                  {state.description || "No description"}
                </StateDescription>
              </StateInfo>
            </StateItem>
          ))}

          {guideLayers.length > 0 && (
            <>
              <Label style={{ marginTop: "16px" }}>
                Guide Layers to Export ({guideLayers.length})
              </Label>
              {guideLayers.map((layer) => (
                <StateItem key={layer.id}>
                  <StateColor color="#2196f3" />
                  <StateInfo>
                    <StateName>{layer.name}</StateName>
                    <StateDescription>
                      {layer.description || "No description"}
                    </StateDescription>
                  </StateInfo>
                </StateItem>
              ))}
            </>
          )}
        </StatesPreview>

        <ButtonGroup>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={!templateName.trim()}
          >
            Export Template
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TemplateExportModal;
